import { useMemo } from "react";
import { Link, matchPath, useLocation } from "react-router";
import { useAuth } from "react-oidc-context";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AnimatePresence, motion } from "framer-motion";
import {
  faHouseChimney,
  faPeopleRoof,
  faCircleQuestion,
  faUser,
  faCode,
  faChevronRight,
  faChartColumn,
  faBuildingUser,
  faCity,
  faDisplayChartUp,
  faUsers,
} from "@fortawesome/pro-regular-svg-icons";

import {
  ROLES,
  REVERSE_LOOKUP_TABLEAU_ROLES,
  PROVIDER_LICENSE_PERMISSIONS,
  PERMISSIONS,
} from "core/constants";

import { Tooltip } from "react-tooltip";
import { useQueryClient } from "@tanstack/react-query";
import Popover from "core/components/Popover";
import Typography from "core/components/Typography";
import useNavigation from "core/hooks/useNavigation";
import useCurrentUser from "core/hooks/useCurrentUser";
import { useCheckFeatureFlag } from "core/hooks/featureFlagging";
import { FLAG_KEYS } from "core/constants";
import {
  ANALYTICS_HUB_DEFAULT_SEARCH,
  ANALYTICS_HUB_PARAM_KEYS,
} from "modules/analyticsHub/constants";
import { useOrganizationByIdQuery, useUserByIdQuery } from "core/queries";
import Logo from "assets/Logo";
import useActiveMembership from "core/hooks/useActiveMembership";
import {
  getEnv,
  hasPermissions,
  ENVIRONMENTS,
  checkUserIsDefinitive,
  hasAnalyticsHubUserAccess,
} from "common/utilities";
import useNavigate from "core/hooks/useNavigate";
import { logoutChannel } from "modules/auth/channels";
import { faScreenUsers } from "@fortawesome/pro-regular-svg-icons";
import { loginToAbsorb } from "modules/admin/actions";
import { faCircleNotch } from "@fortawesome/sharp-solid-svg-icons";
import { useState } from "react";
import { toast } from "sonner";

const collapsedWidth = "64px";
const expandedWidth = "264px";

const NavHeader = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 6px;
  margin-bottom: 15px;
  border-radius: 5px;
  overflow: hidden;

  img,
  svg {
    display: block;
    height: 31px;
    min-width: 31px;
    width: 31px;
  }

  & > div {
    margin-left: 6px;
    color: var(--sidebar-foreground);
    font-family: Inter;
    font-weight: 600;
    opacity: 1;
    transition: opacity 500ms linear;

    div:first-child {
      font-size: 19px;
      letter-spacing: 0.5px;
    }

    div:last-child {
      font-size: 16px;
      letter-spacing: 0.3px;
    }
  }

  .logo-text {
    display: block;
    min-width: 134px;
  }
`;

const NavLink = styled(Link)`
  overflow: hidden;
  contain: strict;
  user-select: none;
  position: relative;
  text-decoration: none;

  & > div {
    margin-bottom: 5px;
    display: flex;
    align-items: center;
    justify-content: flex-start;

    color: var(--sidebar-foreground);
    font-size: 16px;
    height: 40px;
    width: 100%;
    border-radius: 5px;
    white-space: nowrap;
    cursor: pointer;

    :hover {
      background-color: #541299;
    }
  }

  &:hover {
    text-decoration: none;
  }

  ${(props) =>
    props.$isActive &&
    `
    font-weight: 600;

    &:hover {
      background-color: #541299;
    }
  `}

  ${(props) =>
    props.$isLoading &&
    `
    pointer-events: none;

    div {
        background-color: var(--sidebar-background-active);
        transition: none;
    }

    .external-link-content {
        opacity: 0 !important;
    }
  `}
`;

const NavLinkIcon = styled.div`
  margin-left: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const NavLinkTitle = styled.div`
  margin-left: 15px;
  transition: opacity 500ms linear;

  &[data-nav-open="true"] {
    opacity: 1;
  }

  &[data-nav-open="false"] {
    opacity: 0;
  }
`;

const DrawerAside = styled.aside`
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: var(--sidebar-background);
  border-right: 1px solid var(--sidebar-border);
  width: ${collapsedWidth};
  height: 100%;
  padding-top: 20px;
  padding-left: 10px;
  padding-right: 10px;
  padding-bottom: 20px;
  transition: width 500ms ease-in-out;

  ${(props) =>
    props.$isOpen &&
    `
    width: ${expandedWidth};
  `}
`;

const ExternalLink = styled(NavLink)``;

const MainNav = styled.nav`
  width: 100%;
  overflow-x: hidden;
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
`;

const NavToggle = styled.div`
  position: absolute;
  top: 60px;
  right: -13px;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 26px;
  width: 26px;
  background: var(--icon-background);
  border-radius: 9999px;
  /* border: 1px solid var(--text-link); */
  cursor: pointer;
  transition: transform 500ms ease-in-out;

  ${(props) =>
    props.$isNavOpen &&
    `
      transform: rotate(180deg);
  `}
`;

const ProfileMenu = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  border-radius: 5px;
  cursor: pointer;
  overflow: hidden;
  padding: 6px;
  height: 44px;

  width: 100%;
  white-space: nowrap;
  user-select: none;
  transition: opacity 500ms linear;
  color: var(--sidebar-foreground);

  &:hover {
    background-color: var(--sidebar-background-active);
  }
`;

const ProfileTrigger = (props) => {
  const { setRef, isNavOpen, name, email, role, ...triggerProps } = props;

  return (
    <>
      <ProfileMenu
        id="profileMenu"
        $isNavOpen={isNavOpen}
        data-test="profileMenuButton"
        ref={setRef}
        {...triggerProps}
      >
        <div
          style={{
            marginLeft: "2px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              backgroundColor: "#00C1F3",
              borderRadius: "5px",
              width: "28px",
              height: "28px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FontAwesomeIcon
              icon={faUser}
              style={{
                color: "var(--sidebar-foreground)",
                width: "18px",
                height: "18px",
              }}
            />
          </div>
        </div>

        <NavLinkTitle data-nav-open={isNavOpen}>
          <Typography variant="h6" color="white" noMargin>
            {name || email}
          </Typography>

          <Typography variant="sub-text" color="white" noMargin>
            {role}
          </Typography>
        </NavLinkTitle>
      </ProfileMenu>

      <Tooltip
        anchorSelect="#profileMenu"
        children="Settings"
        place="right"
        positionStrategy="absolute"
        offset={20}
        style={{ zIndex: "10000", backgroundColor: "#2A0F4D" }}
        hidden={isNavOpen || triggerProps["aria-expanded"] === "true"}
      />
    </>
  );
};

// Carry the current query string across nav links, but drop the analytics-hub
// page-scoped params so they don't leak onto other routes. Everything else
// (e.g. the membership `n` param) is preserved, and the target route's own
// default search (if any) is merged in.
const buildNavSearch = (routeSearch, currentSearch) => {
  const params = new URLSearchParams(currentSearch);
  ANALYTICS_HUB_PARAM_KEYS.forEach((key) => params.delete(key));
  if (routeSearch) {
    new URLSearchParams(routeSearch).forEach((value, key) => {
      params.set(key, value);
    });
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

const Navigation = () => {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isCheckingAbsorbLogin, setCheckingAbsorbLogin] = useState(false);

  const { isNavOpen, setNavOpen } = useNavigation();
  const { currentUser } = useCurrentUser();
  const { activeMembership } = useActiveMembership();

  const { data: userOrganization } = useOrganizationByIdQuery({
    organizationId: activeMembership.id,
    include: "licenses",
  });
  const hasManagedProvidersFeature = useCheckFeatureFlag(
    FLAG_KEYS.MANAGED_PROVIDERS
  );
  const hasAnalyticsHubFeature = useCheckFeatureFlag(FLAG_KEYS.ANALYTICS_HUB);
  const hasAnalyticsHubAccessLevel =
    hasAnalyticsHubUserAccess(activeMembership);

  const hasBiPortalLicense = useMemo(
    () =>
      Object.values(REVERSE_LOOKUP_TABLEAU_ROLES).includes(
        activeMembership?.tableau_config?.license
      ),
    [activeMembership]
  );
  const biPortalUrl = useMemo(
    () => userOrganization?.onboarding?.bi?.portal_url,
    [userOrganization]
  );
  const biPortal = useMemo(
    () =>
      userOrganization // user and org has loaded
        ? hasBiPortalLicense && biPortalUrl
          ? {
              // valid bi portal link and user license
              disabled: false,
              errorMessage: null,
            }
          : {
              // invalid
              disabled: true,
              errorMessage: !hasBiPortalLicense
                ? "You do not have a BI Portal license assigned." // no BI licence for user
                : "The BI Portal is not set up for your userOrganization.", // no BI portal link for org
            }
        : {
            // user and/or org not loaded yet, disable button
            disabled: true,
            errorMessage: null,
          },
    [userOrganization, hasBiPortalLicense, biPortalUrl]
  );

  const routes = [
    {
      to: "/",
      name: "Home",
      icon: faHouseChimney,
    },
    ...(hasManagedProvidersFeature
      ? [
          {
            to: `/configurations/${activeMembership.id}/aliases`,
            name: "Managed Providers",
            permissions: "configurations:read",
            icon: faPeopleRoof,
            match: "/configurations/:orgId/*",
          },
        ]
      : []),
    ...(hasAnalyticsHubFeature && hasAnalyticsHubAccessLevel
      ? [
          {
            to: "/analytics-hub",
            name: "Analytics Hub",
            permissions: "analytics-hub:read",
            icon: faChartColumn,
            match: "/analytics-hub/*",
            search: ANALYTICS_HUB_DEFAULT_SEARCH,
          },
        ]
      : []),
    {
      to: "/organizations",
      name: "Organizations",
      permissions: "organizations:read",
      icon: faCity,
    },
    {
      to: "/users",
      name: "Users",
      permissions: "all:users:read",
      icon: faUsers,
    },
    {
      to: `/organization/${activeMembership.id}/general`,
      name: "My Organization",
      permissions: "organization:read",
      dynamicParams: {
        userMembershipIds: currentUser.memberships?.map(
          (m) => m.organization_id
        ),
        orgIdParam: activeMembership.id,
      },
      icon: faBuildingUser,
    },
    {
      to: `/analytics/product`,
      name: "Analytics",
      permissions: "analytics:read",
      icon: faDisplayChartUp,
      match: "/analytics/*",
    },
  ];

  const {
    setUserSearchValue,
    setOrgSearchValue,
    setCurrentUserPageNumber,
    setCurrentOrgPageNumber,
  } = useNavigation();

  const onRouteChange = () => {
    setOrgSearchValue("");
    setUserSearchValue("");
    setCurrentUserPageNumber(0);
    setCurrentOrgPageNumber(0);
  };

  const isUserDefinitiveAccount = checkUserIsDefinitive(currentUser.email);
  const isProduction = getEnv() === ENVIRONMENTS.PROD;

  return (
    <DrawerAside $isOpen={isNavOpen}>
      <NavToggle onClick={() => setNavOpen(!isNavOpen)} $isNavOpen={isNavOpen}>
        <FontAwesomeIcon
          icon={faChevronRight}
          style={{ color: "var(--button-contained-foreground)", width: "15px" }}
        />
      </NavToggle>
      <NavHeader data-nav-open={isNavOpen}>
        <img src="/dh-logo-with-text.svg" alt="logo" className="logo-text" />
      </NavHeader>

      <MainNav>
        <div>
          <AnimatePresence initial={false}>
            {routes
              .filter((route) => {
                return hasPermissions(
                  PERMISSIONS[activeMembership.role],
                  route.permissions,
                  route.dynamicParams
                );
              })
              .map(({ to, name, icon, exact, match, search }) => {
                const isActive =
                  to === "/"
                    ? location.pathname === to
                    : exact &&
                      location.pathname.includes(`${activeMembership.id}`)
                    ? location.pathname === to
                    : match
                    ? matchPath(match, location.pathname) // TODO: Use this to simplify isActive
                    : location.pathname.includes(to);

                const id = name.split(" ").join("");

                return (
                  <motion.span
                    key={id}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: 1,
                    }}
                  >
                    <NavLink
                      id={id}
                      to={to + buildNavSearch(search, location.search)}
                      data-nav-open={isNavOpen}
                      $isActive={isActive}
                      onClick={onRouteChange}
                      state={{ from: id }}
                      data-tooltip-id={isNavOpen ? null : id}
                      data-tooltip-content={name}
                      aria-label={name}
                      tabIndex={0}
                    >
                      {isActive && (
                        <motion.div
                          layoutId="activeBackgroundMain"
                          style={{
                            position: "absolute",
                            zIndex: 1,
                            height: "40px",

                            backgroundColor: "#541299",
                            borderRadius: "4px",
                          }}
                        />
                      )}
                      <div style={{ position: "relative", zIndex: 2 }}>
                        <NavLinkIcon data-nav-open={isNavOpen}>
                          <FontAwesomeIcon icon={icon} size="lg" />
                        </NavLinkIcon>

                        <NavLinkTitle data-nav-open={isNavOpen}>
                          {name}
                        </NavLinkTitle>
                      </div>
                    </NavLink>

                    <Tooltip
                      id={id}
                      place="right"
                      positionStrategy="absolute"
                      offset={20}
                      style={{ zIndex: "10000", backgroundColor: "#2A0F4D" }}
                      openEvents={{
                        focus: false, // prevents tooltip from showing if trigger is last focused element (side nav)s
                        mouseover: true,
                      }}
                      role="tooltip"
                    />
                  </motion.span>
                );
              })}
          </AnimatePresence>
        </div>

        {/* Bottom Section */}
        <div>
          {!biPortal.disabled && (
            <ExternalLink
              id="biPortal"
              as="a"
              href={biPortalUrl}
              target="_blank"
              rel="noreferrer"
            >
              <div style={{ position: "relative", zIndex: 2 }}>
                <NavLinkIcon data-nav-open={isNavOpen}>
                  <FontAwesomeIcon
                    icon={faChartColumn}
                    size="lg"
                    style={{ width: "25px" }}
                  />
                </NavLinkIcon>
                <NavLinkTitle data-nav-open={isNavOpen}>BI Portal</NavLinkTitle>
              </div>
            </ExternalLink>
          )}
          {!isNavOpen && (
            <Tooltip
              anchorSelect="#biPortal"
              children="BI Portal"
              place="right"
              positionStrategy="absolute"
              offset={20}
              style={{ zIndex: "10000", backgroundColor: "#2A0F4D" }}
            />
          )}

          <ExternalLink
            id="trainingHub"
            $isLoading={isCheckingAbsorbLogin}
            style={{
              position: "relative",
            }}
            onClick={async (event) => {
              setCheckingAbsorbLogin(true);

              try {
                // Call Absorb API - POST /absorb/login
                await loginToAbsorb({
                  accessToken: auth.user?.access_token,
                });

                if (isProduction) {
                  const url = isUserDefinitiveAccount
                    ? "https://definitive.myabsorb.com"
                    : "https://populi.learning-definitivehc.com";
                  window.open(url, "_blank", "noreferrer");
                } else {
                  const url = isUserDefinitiveAccount
                    ? "https://definitive.sandbox.myabsorb.com"
                    : "https://populi-definitive.sandbox.myabsorb.com";
                  window.open(url, "_blank", "noreferrer");
                }

                // Handle 204
                // We need internal users to be routed here (Any user that exists in entraid, e.g. herman.othelius@definitivehc.com):
                // Stage/QA: https://definitive.sandbox.myabsorb.com

                // And external customers should be routed here:
                // Stage/QA: https://populi-definitive.sandbox.myabsorb.com
              } catch (error) {
                // Handle 501 - User is not allowed to access link
                if (error === "Not supported on this environment") {
                  toast.error(error);
                  return;
                }

                // Handle 400 - User is not allowed to access link
                if (error === "User is not allowed to access link") {
                  console.error("AbsorbError", 400, error);
                  toast.error(error);
                  return;
                }

                // Handle 404 - 'User does not exist. Please reach out to support@populi.ai for assistance.'
                if (
                  error ===
                  "User does not exist. Please reach out to support@populi.ai for assistance."
                ) {
                  console.error("AbsorbError", 404, error);
                  toast.error(error);
                  return;
                }

                console.error("AbsorbError", error);
                toast.error("Something went wrong. Please contact support.");
              } finally {
                setCheckingAbsorbLogin(false);
              }
            }}
          >
            {isCheckingAbsorbLogin && (
              <FontAwesomeIcon
                icon={faCircleNotch}
                spin
                style={{
                  position: "absolute",
                  left: "0px",
                  top: "12px",
                  width: "100%",
                  color: "#fff",
                  zIndex: 3,
                }}
              />
            )}

            <div
              style={{
                position: "relative",
                zIndex: 2,
              }}
            >
              <NavLinkIcon
                className="external-link-content"
                data-nav-open={isNavOpen}
              >
                <FontAwesomeIcon icon={faScreenUsers} size="lg" />
              </NavLinkIcon>
              <NavLinkTitle
                className="external-link-content"
                data-nav-open={isNavOpen}
              >
                Definitive Training Hub
              </NavLinkTitle>
            </div>
          </ExternalLink>

          {!isNavOpen && (
            <Tooltip
              anchorSelect="#trainingHub"
              children="Definitive Training Hub"
              place="right"
              positionStrategy="absolute"
              offset={20}
              style={{ zIndex: "10000", backgroundColor: "#2A0F4D" }}
            />
          )}

          <ExternalLink
            id="apiDocs"
            as="a"
            href="https://populi-prod.web.app/docs/"
            target="_blank"
            rel="noreferrer"
          >
            <div style={{ position: "relative", zIndex: 2 }}>
              <NavLinkIcon data-nav-open={isNavOpen}>
                <FontAwesomeIcon icon={faCode} size="lg" />
              </NavLinkIcon>
              <NavLinkTitle data-nav-open={isNavOpen}>API Docs</NavLinkTitle>
            </div>
          </ExternalLink>

          {!isNavOpen && (
            <Tooltip
              anchorSelect="#apiDocs"
              children="API Docs"
              place="right"
              positionStrategy="absolute"
              offset={20}
              style={{ zIndex: "10000", backgroundColor: "#2A0F4D" }}
            />
          )}

          <ExternalLink
            id="supportPortal"
            as="a"
            href="https://success.populi.ai/hc/en-us"
            target="_blank"
            rel="noreferrer"
          >
            <div style={{ position: "relative", zIndex: 2 }}>
              <NavLinkIcon data-nav-open={isNavOpen}>
                <FontAwesomeIcon
                  icon={faCircleQuestion}
                  size="lg"
                  style={{ width: "25px" }}
                />
              </NavLinkIcon>
              <NavLinkTitle data-nav-open={isNavOpen}>
                Support Portal
              </NavLinkTitle>
            </div>
          </ExternalLink>

          {!isNavOpen && (
            <Tooltip
              anchorSelect="#supportPortal"
              children="Support Portal"
              place="right"
              positionStrategy="absolute"
              offset={20}
              style={{ zIndex: "10000", backgroundColor: "#2A0F4D" }}
            />
          )}

          <Popover
            placement="right-end"
            offset={20}
            navProfilePopover={true}
            isNavOpen={isNavOpen}
            TriggerComponent={ProfileTrigger}
            /**
             * Custom props
             */
            name={currentUser.name}
            email={currentUser.email}
            role={activeMembership.role}
            /**
             * Menu actions
             */
            actions={[
              {
                name: "View Profile",
                onClick: (event, setPopoverOpen) => {
                  navigate(`/user-profile/${currentUser.userId}`, {
                    // TODO: Seems wrong to use state for values that exist in the url params
                    state: {
                      from: location?.state?.from || window.history.state.from,
                      orgId: location?.state?.orgId,
                      userEmail: location?.state?.userEmail,
                    },
                  });
                  setPopoverOpen(false);
                },
              },
              ...([ROLES.systemAdmin, ROLES.systemViewer].includes(
                activeMembership.role
              )
                ? [
                    {
                      name: [ROLES.systemViewer].includes(activeMembership.role)
                        ? "View Organizations"
                        : "Manage Organizations",
                      onClick: (event, setPopoverOpen) => {
                        navigate("/organizations");
                        setPopoverOpen(false);
                      },
                    },
                  ]
                : []),
              ...([ROLES.orgAdmin].includes(activeMembership.role)
                ? [
                    {
                      name: "My Organization",
                      onClick: (event, setPopoverOpen) => {
                        navigate(`/organization/${activeMembership.id}`);
                        setPopoverOpen(false);
                      },
                    },
                  ]
                : []),
              {
                name: "Logout",
                dataTest: "logoutMenuLink",
                onClick: () => {
                  queryClient.clear();
                  logoutChannel?.postMessage("logoutAllTabs");
                  setTimeout(async () => {
                    auth.signoutRedirect();
                  }, 250);
                },
              },
            ]}
          />
        </div>
      </MainNav>
    </DrawerAside>
  );
};

export default Navigation;
